<template>
  <section
    v-if="budgetStatus && (budgetStatus.isEnrolled || budgetStatus.isEligible)"
    class="my-account__full-width-section my-account__section-with-bottom-border gds-space-stack-l"
  >
    <div class="gds-flex-container gds-flex-container--space-between gds-flex-container--top gds-space-stack-m">
      <div class="my-account__on-off-spacer-right gds-flex-container gds-flex-container--top gds-space-stack-m">
        <h2 class="gds-display-1 gds-space-inline-m">Budget Billing</h2>
        <div v-if="!editing" class="gds-flex-container gds-flex-container--left">
          <div class="my-account__on-off-text gds-flex-container gds-flex-container--left gds-flex-container--top">
            <IndicatorOnOff :value="budgetStatus.isEnrolled"></IndicatorOnOff>
          </div>
        </div>
      </div>

      <div class="edit-close-button-combo">
        <button v-if="!editing" @click="editing = true" type="button" class="gds-button gds-text-button gds-space-inline-l">
          <span class="my-account__notifications--text-button gds-button__text">Edit</span>
        </button>
        <button v-if="editing && !pendingSave" @click="editing = false" type="button" class="gds-button gds-text-button gds-space-inline-l">
          <span class="my-account__notifications--text-button gds-button__text">Close</span>
        </button>
      </div>
    </div>
    <!-- Budget Billing Display -->
    <div v-if="!editing">
      <div v-if="budgetStatus.isEnrolled" class="gds-font-size-m gds-space-stack-m">
        You are enrolled in Budget Billing.
        <span v-if="budgetStatus.totalBudgetAmount">Your monthly bill is {{budgetStatus.totalBudgetAmount | currency}}.</span>
      </div>
      <div
        v-if="!budgetStatus.isEnrolled && budgetStatus.isEligible"
        class="gds-font-size-m gds-space-stack-m gds-text-grey"
      >Enroll in budget billing and get consistent monthly bills of {{budgetStatus.totalBudgetAmount | currency}}</div>
      <div
        v-if="!budgetStatus.isEnrolled && !budgetStatus.isEligible && !budgetStatus.ineligibleReason"
        class="gds-font-size-m gds-space-stack-m gds-text-grey"
      >You are ineligible for Budget Billing</div>
      <div
        v-if="!budgetStatus.isEnrolled && !budgetStatus.isEligible && budgetStatus.ineligibleReason"
        class="gds-font-size-m gds-space-stack-m gds-text-grey"
      >{{budgetStatus.ineligibleReason}}</div>
    </div>
    <!-- Budget Billing Edit -->
    <div v-if="editing">
      <div v-if="budgetStatus.isEnrolled">
        <div class="gds-font-size-m gds-space-stack-m">
          To turn off Budget Billing please contact customer service at
          <span class="gds-nobreak">{{GMPPhoneNumber}}.</span>
        </div>
      </div>
      <form v-if="!budgetStatus.isEnrolled" @submit.prevent="TurnOnBudget()">
        <div class="my-account__header-button-pairing gds-flex-container gds-flex-container--left">
          <fieldset class="gds-fieldset gds-space-inline-m">
            <label class="gds-switch">
              <input v-model="budgetEnrolled" type="checkbox" />
              <span class="gds-switch__faux"></span>
            </label>
          </fieldset>
          <div>
            <div v-if="!budgetEnrolled" class="gds-font-size-l gds-space-stack-m">You are not enrolled in budget billing.</div>
            <div v-if="budgetEnrolled" class="gds-font-size-l gds-space-stack-m">You will be enrolled in budget billing. Click save to confirm.</div>
          </div>
        </div>
        <div class="gds-font-size-l gds-font-demi gds-space-stack-m">If you enroll in budget billing, your monthly bill will be {{budgetStatus.totalBudgetAmount | currency}}.</div>

        <div class="my-account__description--limit-width gds-font-size-m gds-space-stack-m">
          Budget billing is not a discount. Your consistent monthly payment is based on your average usage and is subject to change.
          If you are a new customer or have net metering, we do not recommend going on a budget. We track your actual balance and review your account on a regular basis.
          If you stop Budget Billing, your next bill will include an adjustment to true up your account with your actual balance.
        </div>
        <button type="submit" :disabled="pendingSave || !budgetEnrolled" :class="{ 'gds-loading': pendingSave }" class="gds-button gds-round gds-space-stack-m">
          <span class="gds-button__text">Save</span>
        </button>
        <div v-if="errorSave" class="form-message-box gds-space-stack-m">There was an error enabling Budget Billing.</div>
      </form>
    </div>
  </section>
</template>

<script>
import IndicatorOnOff from "../../generic/IndicatorOnOff";
import GMPAPI from "../../../services/gmpapi";
import { GMPPhoneNumber } from "../../../environment"
import { DumpError } from "../../../utilities";

export default {
  name: "EditorBudgetBilling",
  data() {
    return {
      editing: false,
      pendingSave: false,
      errorSave: false,

      budgetStatus: undefined,
      // Always starts false, because you can't edit unless you're not enrolled
      budgetEnrolled: false,

      GMPPhoneNumber,
    };
  },
  components: {
    IndicatorOnOff,
  },
  computed: {
    currentAccount() {
      return this.$store.state.user.currentAccount;
    },
  },
  async mounted() {
    await this.RefreshStatus();
  },
  methods: {
    async RefreshStatus() {
      this.budgetStatus = undefined;
      if (!this.currentAccount) {
        return;
      }
      try {
        const status = await GMPAPI.GetBudgetStatus(this.currentAccount.accountNumber)
        this.budgetStatus = status;
      } catch (err) {
        DumpError("Budget billing editor refresh error", err);
        // Note that we do not have an error state for this card because if not fully loaded, it is just invisible
      }
    },
    async TurnOnBudget() {
      this.pendingSave = true;
      this.errorSave = undefined;
      try {
        await GMPAPI.CreateBudgetBilling(this.currentAccount.accountNumber);
        await this.RefreshStatus();
        this.editing = false;
      } catch (err) {
        DumpError("Create budget error", err);
        this.errorSave = true;
      } finally {
        this.pendingSave = false;
      }
    },
  },
  watch: {
    // Refresh status whenever the account changes
    async currentAccount() {
      this.editing = false;
      this.errorSave = false;
      await this.RefreshStatus();
    }
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>